<template>
  <div class="afc" :class="['is-' + file.kind, { 'is-gone': gone }]">
    <span class="afc-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
      <b>{{ file.kind === "pdf" ? "PDF" : "XLSX" }}</b>
    </span>
    <div class="afc-text">
      <div class="afc-name" :title="file.name">{{ file.name }}</div>
      <div class="afc-meta">{{ meta }}</div>
    </div>
    <div class="afc-acts">
      <button type="button" class="afc-btn" :disabled="busy || gone" @click="download">{{ busy ? $tr("Preparing…") : gone ? $tr("Not kept") : $tr("Download") }}</button>
      <button type="button" class="afc-del" v-if="deletable" title="Delete" aria-label="Delete" :disabled="busy" @click="$emit('delete', file)"><AppIcon name="x" /></button>
    </div>
  </div>
</template>

<script>
import { tr, trf, dateLocale } from "@/i18n";

/**
 * A PDF report or an Excel file Connect made (2026-10-20), under its reply and in Files. Download asks the
 * API for the file's document and builds the file here (utils/assistantDocs), so "Preparing…" covers the
 * moment the PDF or spreadsheet library loads.
 */
export default {
  name: "AssistantFileCard",
  props: {
    file: { type: Object, required: true },
    deletable: { type: Boolean, default: false },
  },
  emits: ["delete"],
  data() {
    return { busy: false, missing: false };
  },
  computed: {
    expired() { return !!this.file.expires && new Date(this.file.expires).getTime() < Date.now(); },
    gone() { return this.missing || this.expired || this.$store.state.assistant.filesGone.indexOf(this.file.id) !== -1; },
    meta() {
      const kind = this.file.kind === "pdf" ? tr("PDF report") : tr("Excel file");
      if (this.gone) return trf("{0} · no longer kept", [kind]);
      const until = new Date(this.file.expires);
      return isNaN(until) ? kind : trf("{0} · kept until {1}", [kind, until.toLocaleDateString(dateLocale("en-CA"), { month: "short", day: "numeric" })]);
    },
  },
  methods: {
    async download() {
      if (this.busy || this.gone) return;
      this.busy = true;
      const ok = await this.$store.dispatch("assistantFileDownload", this.file);
      this.busy = false;
      if (ok === false) this.missing = true;
    },
  },
};
</script>

<style scoped>
.afc { box-sizing: border-box; display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 12px; border: 1px solid var(--border-primary); border-radius: 14px; background: var(--bg-primary); box-shadow: var(--shadow-sm); }
.afc-icon { position: relative; flex: none; width: 34px; height: 40px; color: var(--text-muted); }
.afc-icon svg { width: 34px; height: 40px; fill: none; stroke: currentColor; stroke-width: 1.4; stroke-linejoin: round; }
.afc-icon b { position: absolute; left: 50%; bottom: 7px; transform: translateX(-50%); padding: 0 3px; border-radius: 3px; font-size: 8px; font-weight: 700; line-height: 12px; letter-spacing: 0.02em; color: #fff; }
.afc.is-pdf .afc-icon b { background: #dc2626; }
.afc.is-xlsx .afc-icon b { background: #15803d; }
.afc-text { flex: 1; min-width: 0; }
.afc-name { font-size: var(--t-body-sm); font-weight: 600; line-height: 1.4; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.afc-meta { font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.afc-acts { flex: none; display: flex; align-items: center; gap: 4px; }
.afc-btn { min-height: 30px; padding: 4px 14px; border: 1px solid var(--btn-primary-bg); border-radius: 999px; background: var(--btn-primary-bg); color: var(--btn-primary-text); font: inherit; font-size: var(--t-caption); font-weight: 600; cursor: pointer; white-space: nowrap; }
.afc-btn:hover:not(:disabled) { background: var(--btn-primary-hover-bg); border-color: var(--btn-primary-hover-bg); }
.afc-btn:disabled { opacity: 0.55; cursor: default; }
.afc.is-gone .afc-btn { border-color: var(--border-primary); background: transparent; color: var(--text-muted); }
.afc-del { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: var(--text-muted); cursor: pointer; }
.afc-del:hover:not(:disabled) { background: var(--danger-bg); color: var(--danger); }
.afc-del :deep(svg) { width: 14px; height: 14px; }
</style>
